import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import ChatPageHeader from '@/components/chat/ChatPageHeader';
import ChatSearch from '@/components/chat/ChatSearch';
import ChatFilters from '@/components/chat/ChatFilters';
import ChatTabs from '@/components/chat/ChatTabs';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { filterEnabledModels } from '@/lib/modelSettings';
import LoadingScreen from '@/components/ui/loading-screen';
import { useAppStore } from '@/store';
import SEO from '@/components/common/SEO';
import RuVectorStatusCard from '@/components/ruvector/RuVectorStatusCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BrainCircuit, Gamepad2, Laptop, Network } from 'lucide-react';

const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const cleaned = uuid.trim();
  console.log("Validating UUID:", JSON.stringify(cleaned));
  return uuidRegex.test(cleaned);
};

const Index = () => {
  const navigate = useNavigate();
  const { chats, refreshChats, addChat, models, chatHasMore, loadMoreChats } = useSupabaseStore();

  const { user } = useAuth(); // This is the Supabase auth user, not the UserProfile from useAppStore
  const { user: appUser } = useAppStore(); // Get the UserProfile from useAppStore
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModels, setFilterModels] = useState<string[]>([]);
  const [showEmpty, setShowEmpty] = useState(true);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [previewsLoading, setPreviewsLoading] = useState(true);
  const visibleModels = useMemo(() => filterEnabledModels(models), [models]);
  
  useEffect(() => {
    if (user) {
      refreshChats();
    }
  }, [user]);

  useEffect(() => {
    const visibleModelIds = new Set(visibleModels.map((model) => model.id));
    setFilterModels((current) => current.filter((modelId) => visibleModelIds.has(modelId)));
  }, [visibleModels]);

  useEffect(() => {
    if (chats.length > 0) {
      const timer = setTimeout(() => {
        setPreviewsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    setPreviewsLoading(false);
  }, [chats]);

  // Use user's default model if set, otherwise fallback to free tier or first model
  const defaultModel = useMemo(() => {
    // 1. Prioritize the user's explicitly set default model from their profile (appUser)
    if (appUser && appUser.defaultModelApiName) { // Changed to defaultModelApiName
      const found = visibleModels.find(m => m.apiName === appUser.defaultModelApiName); // Find by apiName
      if (found) {
        console.log("Default model selected: User's preference", found.name);
        return found;
      }
    }

    // 2. If no user default or not found, try to find 'llama3'
    const llama3Model = visibleModels.find(m => m.apiName === 'llama3' || m.name.toLowerCase().includes('llama3'));
    if (llama3Model) {
      console.log("Default model selected: Llama3 (global fallback)", llama3Model.name);
      return llama3Model;
    }

    // 3. Fallback to free tier or first available model
    const fallbackModel = visibleModels.find(m => m.tier === 'free') || visibleModels[0];
    if (fallbackModel) {
      console.log("Default model selected: Fallback (free tier or first available)", fallbackModel.name);
    } else {
      //console.warn("No default model could be selected. No models available.");
    }
    return fallbackModel;
  }, [visibleModels, appUser]);

  const filteredChats = useMemo(() => {
    return chats
      .filter(chat => {
        if (searchTerm) {
          const titleMatch = chat.title.toLowerCase().includes(searchTerm.toLowerCase());
          const messages = chat.messages || [];
          const contentMatch = messages.some(message => 
            message.content && message.content.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (!titleMatch && !contentMatch) {
            return false;
          }
        }
        if (filterModels.length > 0 && !filterModels.includes(chat.modelId)) {
          return false;
        }
        if (!showEmpty && chat.messages && chat.messages.length === 0) {
          return false;
        }
        return true;
      });
  }, [chats, searchTerm, filterModels, showEmpty]);
  
  const handleNewChat = useCallback(async () => {
    if (!user || !defaultModel || isCreatingChat) {
      console.log("Cannot create chat:", { user, defaultModel, isCreatingChat });
      if (!defaultModel) {
        toast({
          title: "No AI Model Available",
          description: "Cannot create a new chat because no AI models are configured.",
          variant: "destructive"
        });
      }
      return;
    }
    setIsCreatingChat(true);
    try {
      const newChat = await addChat(user.id, 'New Chat', defaultModel.id);
      if (!newChat || !newChat.id) {
        throw new Error("Failed to create chat - no chat object returned");
      }
      
      console.log("Chat created successfully, chatId:", newChat.id);
      navigate(`/chat/${newChat.id}`);

    } catch (error) {
      console.error('Error creating new chat:', error);
      toast({
        title: "Error",
        description: "Failed to create a new chat. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [user, defaultModel, navigate, isCreatingChat, addChat, refreshChats]);
  
  const favoriteChats = useMemo(() => {
    return filteredChats.filter(chat => chat.isFavorite === true);
  }, [filteredChats]);
  
  const recentChats = useMemo(() => {
    return [...filteredChats].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [filteredChats]);
  
  const popularChats = useMemo(() => {
    return [...filteredChats].sort(
      (a, b) => (b.viewCount || 0) - (a.viewCount || 0)
    );
  }, [filteredChats]);
  
  const handleLoadMore = useCallback(async () => {
    if (!chatHasMore) return;
    setPreviewsLoading(true);
    try {
      await loadMoreChats();
    } catch (error) {
      console.error('Error loading more chats:', error);
    } finally {
      setPreviewsLoading(false);
    }
  }, [loadMoreChats, chatHasMore]);
  
  if (chats.length === 0) {
    return (
      <MainLayout>
        <div className="container px-4 py-6 max-w-7xl mx-auto">
          <ChatPageHeader onNewChat={handleNewChat} />
          <div className="mt-8 flex flex-col items-center justify-center">
            <LoadingScreen />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO
        title="Home"
        description="Welcome to mapleos — your digital canvas for chatting, coding, and creating."

      />
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="flex flex-col space-y-4">
          <ChatPageHeader onNewChat={handleNewChat} />
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 via-background to-emerald-500/5">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">New</Badge>
                  <Badge variant="outline">Browser + Electron</Badge>
                </div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Laptop className="h-5 w-5 text-primary" />
                  MapleClaw
                </CardTitle>
                <CardDescription>
                  A local MapleOS control plane with durable sessions, transcript history, heartbeat events, and a dedicated Blueprint Gateway node.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Use it in the browser or MapleOS desktop app to drive Blueprint runs through the same local session model.</p>
                  <p className="flex items-center gap-2"><Network className="h-4 w-4" /> No separate daemon required. Electron gets richer local persistence.</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/desktop/start?open=mapleclaw')}>
                  Open MapleClaw in MapleOS
                </Button>
              </CardContent>
            </Card>

            <Card className="border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-background to-cyan-500/10">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">Cortex</Badge>
                  <Badge variant="outline">MapleOS Game</Badge>
                </div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Gamepad2 className="h-5 w-5 text-amber-500" />
                  Cortex — Play Now
                </CardTitle>
                <CardDescription>
                  Jump straight into the new Cortex menu system with singleplayer, map selection, options, and multiplayer hosting or joining flows.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Boot directly into MapleOS and launch the updated Cortex experience from the Games folder.</p>
                  <p>Includes the new main menu, lobby flow, map browser, and host/join multiplayer surfaces.</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/desktop/start?open=cortex')}>
                  Launch Cortex in MapleOS
                </Button>
              </CardContent>
            </Card>

            <Card className="border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 via-background to-sky-500/10 lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">DeMythos</Badge>
                  <Badge variant="outline">Looped Reasoning</Badge>
                </div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <BrainCircuit className="h-5 w-5 text-emerald-500" />
                  DeMythos — Terminal-Native Deep Reasoning
                </CardTitle>
                <CardDescription>
                  Launch DeMythos inside MapleOS to run planner → critic → refiner loops with live transcripts, adjustable budgets, and Blueprint-ready structured outputs.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Use it from Terminal with <span className="font-mono text-foreground">demythos</span> or open it directly from the AI folder.</p>
                  <p>Blueprint Studio now exposes DeMythos as a dedicated reasoning node with transcript, usage, and loop-state outputs.</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/desktop/start?open=demythos')}>
                  Open DeMythos in MapleOS
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-center">
            <ChatSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
            <ChatFilters
              showEmpty={showEmpty}
              setShowEmpty={setShowEmpty}
              filterModels={filterModels}
              setFilterModels={setFilterModels}
              models={visibleModels}
            />
          </div>

          <ChatTabs 
            recentChats={recentChats}
            favoriteChats={favoriteChats}
            popularChats={popularChats}
            handleNewChat={handleNewChat}
            loadingMessages={previewsLoading}
            onLoadMore={handleLoadMore}
            hasMore={chatHasMore}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default Index;